<template>
  <div class="routes-list" :class="{ 'is-compact': variant === 'compact' }">
    <!-- Optional title (hidden in compact mode) -->
    <div v-if="title && variant !== 'compact'" class="routes-list-header">
      <span class="routes-list-title">{{ title }}</span>
    </div>

    <!-- Routes list -->
    <div class="routes-list-content">
      <div
        v-for="route in routes"
        :key="route.name"
        class="route-item"
        :class="{
          'is-active': isRouteActive(route),
        }"
        @click="handleRouteClick(route)"
        :title="variant === 'compact' ? getRouteTitle(route) : undefined"
      >
        <!-- Icône à gauche (ou dot fallback si pas d'icône) -->
        <IconRenderer
          v-if="getRouteIcon(route)"
          :name="getRouteIcon(route)"
          size="small"
          type="ionic"
          class="route-icon-left"
        />
        <div v-else class="route-dot"></div>

        <!-- Contenu texte (caché en mode compact) -->
        <div v-if="variant !== 'compact'" class="route-content">
          <div class="route-title-container">
            <span class="route-title">{{ getRouteTitle(route) }}</span>
            <span v-if="showDescription && getRouteDescription(route)" class="route-description">
              {{ getRouteDescription(route) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

interface RouteItem {
  name: string;
  path: string;
  meta?: {
    title?: string;
    icon?: string;
    description?: string;
  };
}

interface Props {
  /** Routes à afficher */
  routes: RouteItem[];
  /** Titre optionnel de la section */
  title?: string;
  /** Variante d'affichage */
  variant?: 'default' | 'compact';
  /** Afficher les descriptions */
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  variant: 'default',
  showDescription: false,
});

const emit = defineEmits<{
  (e: 'route-click', route: RouteItem): void;
}>();

const router = useRouter();
const currentRoute = useRoute();
const { t } = useI18n();

/**
 * Vérifie si la route est active
 */
function isRouteActive(route: RouteItem): boolean {
  return currentRoute.name === route.name;
}

/**
 * Récupère le titre traduit de la route
 */
function getRouteTitle(route: RouteItem): string {
  if (route.meta?.title) {
    // Si c'est une clé de traduction
    const translated = t(route.meta.title, route.meta.title);
    return translated !== route.meta.title ? translated : route.meta.title;
  }
  // Fallback sur le nom de la route formaté
  return route.name.replace(/([A-Z])/g, ' $1').trim();
}

/**
 * Récupère la description traduite de la route
 */
function getRouteDescription(route: RouteItem): string | undefined {
  if (route.meta?.description) {
    return t(route.meta.description, '');
  }
  return undefined;
}

/**
 * Récupère l'icône de la route
 */
function getRouteIcon(route: RouteItem): string | undefined {
  return route.meta?.icon;
}

/**
 * Gère le clic sur une route
 */
function handleRouteClick(route: RouteItem) {
  emit('route-click', route);
  router.push({ name: route.name });
}
</script>

<style scoped>
.routes-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 20px;
}

.routes-list-header {
  padding: 8px 12px 4px;
}

.routes-list-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--ion-color-medium);
}

.routes-list-content {
  display: flex;
  flex-direction: column;
}

.route-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border-left: solid 1px rgba(var(--ion-color-primary-rgb), 0.1);
}

.route-item:hover {
  background: rgba(var(--ion-color-tertiary-rgb), 0.08);
  transform: translateX(4px);
  border-radius: 0 2vmin 2vmin 0;
}

.route-item.is-active {
  background: rgba(var(--ion-color-primary-rgb), 0.1);
  border-left-color: var(--ion-color-primary);
}

.route-item.is-active .route-dot {
  background: var(--ion-color-primary);
  transform: scale(1.3);
}

.route-item.is-active .route-title {
  color: var(--ion-color-primary);
  font-weight: 600;
}

.route-dot {
  width: 6px;
  height: 6px;
  background: var(--ion-color-tertiary);
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.route-item:hover .route-dot {
  transform: scale(1.3);
  background: var(--ion-color-primary);
}

.route-icon-left {
  color: var(--ion-color-tertiary);
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 18px;
}

.route-item:hover .route-icon-left {
  color: var(--ion-color-primary);
  transform: scale(1.1);
}

.route-item.is-active .route-icon-left {
  color: var(--ion-color-primary);
  transform: scale(1.1);
}

.route-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.route-title-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.route-title {
  font-size: 14px;
  color: var(--ion-text-color);
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.route-description {
  font-size: 11px;
  color: var(--ion-color-medium);
  line-height: 1.3;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.route-icon {
  color: var(--ion-color-medium);
  opacity: 0.7;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.route-item:hover .route-icon {
  opacity: 1;
  color: var(--ion-color-primary);
}

/* Variant: compact - icônes seulement avec tooltip */
.routes-list.is-compact .route-item {
  padding: 10px;
  justify-content: center;
  border-left: none;
  border-radius: 8px;
}

.routes-list.is-compact .route-item:hover {
  transform: scale(1.1);
  border-radius: 8px;
}

.routes-list.is-compact .route-icon-left {
  font-size: 20px;
}

.routes-list.is-compact .route-dot {
  width: 8px;
  height: 8px;
}

/* Animation */
.route-item {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .route-item {
    padding: 12px;
  }

  .route-title {
    font-size: 15px;
  }
}
</style>
